import { useEffect, useRef, useState, type FC } from 'react';
import { Typography, Avatar } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import type { Testimonial } from '@/types';

const { Paragraph, Text } = Typography;

interface TestimonialCardProps {
  testimonial: Testimonial;
}

export const TestimonialCard: FC<TestimonialCardProps> = ({ testimonial }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [fadeTop, setFadeTop] = useState(false);
  const [fadeBottom, setFadeBottom] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const overflows = el.scrollHeight > el.clientHeight + 1;
      const atTop = el.scrollTop <= 0;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setFadeTop(overflows && !atTop);
      setFadeBottom(overflows && !atBottom);
    };

    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="testimonial-card hoverable-card">
      <div
        className={`testimonial-card__scroll${fadeTop ? ' is-fade-top' : ''}${fadeBottom ? ' is-fade-bottom' : ''}`}
        ref={scrollRef}
      >
        <Paragraph
          style={{
            fontSize: 15,
            lineHeight: 1.8,
            fontStyle: 'italic',
            marginBottom: 0,
          }}
        >
          &ldquo;{testimonial.content}&rdquo;
        </Paragraph>
      </div>

      {/* Scroll hint — static down arrow, shown only while there's more to scroll */}
      <div
        className={`testimonial-card__scroll-hint${fadeBottom ? ' is-visible' : ''}`}
        aria-hidden="true"
      >
        <DownOutlined />
      </div>

      <div className="testimonial-card__author">
        <Avatar
          size={40}
          src={testimonial.avatar || undefined}
          style={{
            background: testimonial.avatar ? 'transparent' : 'var(--color-accent)',
            fontSize: 16,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {!testimonial.avatar && testimonial.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .slice(0, 2)
            .toUpperCase()}
        </Avatar>
        <div>
          <Text strong style={{ display: 'block', fontSize: 14 }}>
            {testimonial.name}
          </Text>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {testimonial.role}, {testimonial.company}
          </Text>
        </div>
      </div>
    </div>
  );
};
